/**
 * ShareCard — 9:16 (1080×1920) share card for Moon Phase Soulmate.
 *
 * T23: Off-screen rendering surface for html-to-image capture.
 *
 * This component uses ABSOLUTE pixel dimensions (not viewport units) so that
 * html-to-image / toPng() can rasterize it at correct resolution regardless
 * of where it is positioned in the DOM.
 *
 * The static starfield is rendered as an inline SVG with 80 deterministically
 * positioned stars so the card is identical across renders (no Math.random).
 *
 * MoonOrb uses only SVG/CSS — no canvas, no external font — safe for html-to-image.
 */

import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MoonOrb } from '../../brand/MoonOrb';
import type { MoonPhaseOutput } from '../../../hooks/useRunMoonPhase';
import styles from './ShareCard.module.css';

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface ShareCardProps {
  output: MoonPhaseOutput;
  /** Moon phase slug, e.g. "full", "waning-crescent". Drives MoonOrb rendering. */
  phase?: string;
}

// --------------------------------------------------------------------------
// Phase slug → float helper
// --------------------------------------------------------------------------

function phaseToFloat(slug: string): number {
  const map: Record<string, number> = {
    'new':              0,
    'new-moon':         0,
    'new_moon':         0,
    'waxing-crescent':  0.125,
    'waxing_crescent':  0.125,
    'first-quarter':    0.25,
    'first_quarter':    0.25,
    'waxing-gibbous':   0.375,
    'waxing_gibbous':   0.375,
    'full':             0.5,
    'full-moon':        0.5,
    'full_moon':        0.5,
    'waning-gibbous':   0.625,
    'waning_gibbous':   0.625,
    'last-quarter':     0.75,
    'last_quarter':     0.75,
    'third-quarter':    0.75,
    'third_quarter':    0.75,
    'waning-crescent':  0.875,
    'waning_crescent':  0.875,
  };
  const key = slug.toLowerCase().trim();
  return map[key] ?? 0.5;
}

// --------------------------------------------------------------------------
// Deterministic starfield helpers (seeded — no Math.random)
// --------------------------------------------------------------------------

function seed(n: number): number {
  const x = Math.sin(n * 9999) * 10000;
  return x - Math.floor(x);
}

interface StarDef {
  cx: number;
  cy: number;
  r: number;
  bright: boolean;
}

function buildStars(count: number, w: number, h: number): StarDef[] {
  return Array.from({ length: count }).map((_, i) => ({
    cx: seed(i * 2.13) * w,
    cy: seed(i * 3.71) * h,
    r:  seed(i * 5.31) * 2.4 + 0.4,
    bright: seed(i * 11) > 0.80,
  }));
}

const STARS = buildStars(80, 1080, 1920);

// --------------------------------------------------------------------------
// Phase glyph map
// --------------------------------------------------------------------------

const PHASE_GLYPHS: Record<string, string> = {
  new_moon:         '🌑',
  waxing_crescent:  '🌒',
  first_quarter:    '🌓',
  waxing_gibbous:   '🌔',
  full_moon:        '🌕',
  waning_gibbous:   '🌖',
  third_quarter:    '🌗',
  waning_crescent:  '🌘',
};

function getMoonGlyph(phase: string): string {
  const key = phase.toLowerCase().replace(/[\s-]+/g, '_');
  return PHASE_GLYPHS[key] ?? '🌕';
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

export const ShareCard = forwardRef<HTMLDivElement, ShareCardProps>(
  function ShareCard({ output, phase }, ref) {
    const { t } = useTranslation('flows');
    const glyph = getMoonGlyph(output.phase);
    const illuminationPct = Math.round(output.illumination * 100);
    const lunarAge = output.lunarAge.toFixed(1);

    // Resolve phase float: prefer explicit prop, fall back to output.phase slug
    const phaseSlug = phase ?? output.phase;
    const phaseFloat = phaseToFloat(phaseSlug);

    return (
      <div ref={ref} className={styles.card} aria-hidden="true">
        {/* Static starfield */}
        <svg
          className={styles.starfield}
          viewBox="0 0 1080 1920"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {STARS.map((s, i) => (
            <circle
              key={i}
              cx={s.cx}
              cy={s.cy}
              r={s.r}
              fill={s.bright ? '#f0c850' : '#f5e6d3'}
              opacity={s.bright ? 0.9 : 0.45}
            />
          ))}

          {/* 3 signature bright points with glow halos */}
          <circle cx={238} cy={230}  r={3}  fill="#f5e6d3" opacity={0.95} />
          <circle cx={238} cy={230}  r={30} fill="#f5e6d3" opacity={0.08} />
          <circle cx={843} cy={806}  r={3}  fill="#f0c850" opacity={0.95} />
          <circle cx={843} cy={806}  r={24} fill="#f0c850" opacity={0.10} />
          <circle cx={952} cy={1497} r={2}  fill="#e89a3c" opacity={0.90} />
          <circle cx={952} cy={1497} r={20} fill="#e89a3c" opacity={0.09} />
        </svg>

        {/* Content */}
        <div className={styles.content}>
          {/* Eyebrow */}
          <p className={styles.eyebrow}>CALESTEA · MMXXVI</p>

          {/* Triadic title block */}
          <div className={styles.titleBlock}>
            <p className={styles.titleLine}>{t('moonPhase.shareCard.titleBornUnder')}</p>
            <p className={styles.titleLineItalic}>{glyph} {output.phaseName}</p>
            <p className={styles.titleLine}>{t('moonPhase.shareCard.titleSoulmateRite')}</p>
          </div>

          {/* Moon orb — replaces LivingSigil */}
          <div data-testid="share-card-moon-orb">
            <MoonOrb phase={phaseFloat} size={120} />
          </div>

          {/* Phase name */}
          <p className={styles.phaseName}>{output.phaseName}</p>

          {/* Archetype name */}
          <p className={styles.archetypeName}>{output.archetypeName}</p>

          {/* Illumination + lunar age readout */}
          <div className={styles.readoutRow}>
            <div className={styles.readoutItem}>
              <span className={styles.readoutLabel}>{t('moonPhase.reveal.illuminationLabel')}</span>
              <span className={styles.readoutValue}>{illuminationPct}%</span>
            </div>
            <div className={styles.readoutItem}>
              <span className={styles.readoutLabel}>{t('moonPhase.reveal.lunarAgeLabel')}</span>
              <span className={styles.readoutValue}>{lunarAge}d</span>
            </div>
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <div className={styles.footerRule} />
            <p className={styles.footerUrl}>app.calestea.com</p>
          </div>
        </div>
      </div>
    );
  },
);
